import React from 'react';
import { motion } from 'framer-motion';
import { Search, PenTool, Braces, BadgeAlert, Rocket } from 'lucide-react';

export default function ProcessTimeline() {
  const steps = [
    {
      number: '01',
      title: 'Discovery & Strategy',
      description: 'We audit your existing platform, refine your software scope, draw initial workflow architectures, and establish a solid timeline roadmap.',
      icon: Search,
      color: 'text-[#8B6B4A]',
      bgColor: 'bg-[#8B6B4A]/5',
      borderGlow: 'hover:border-[#8B6B4A]/30'
    },
    {
      number: '02',
      title: 'Premium Design',
      description: 'We design premium, bespoke high-fidelity UI screens in Figma, mapping out interactive click-through user flows to perfect the user experience.',
      icon: PenTool,
      color: 'text-[#C8A97E]',
      bgColor: 'bg-[#C8A97E]/5',
      borderGlow: 'hover:border-[#C8A97E]/30'
    },
    {
      number: '03',
      title: 'Expert Development',
      description: 'Our senior developers write clean, modular React frontend interfaces and robust backend APIs, ensuring full compatibility and speed.',
      icon: Braces,
      color: 'text-[#8B6B4A]',
      bgColor: 'bg-[#8B6B4A]/5',
      borderGlow: 'hover:border-[#8B6B4A]/30'
    },
    {
      number: '04',
      title: 'Rigorous QA Testing',
      description: 'We run comprehensive unit tests, security audits, page speed benchmarks, and responsive screen size checks to guarantee zero bugs.',
      icon: BadgeAlert,
      color: 'text-[#C8A97E]',
      bgColor: 'bg-[#C8A97E]/5',
      borderGlow: 'hover:border-[#C8A97E]/30'
    },
    {
      number: '05',
      title: 'Secure Cloud Launch',
      description: 'We set up auto-scaling containers, enable HTTPS encryption, initialize backups, configure analytics tracking, and push the platform live.',
      icon: Rocket,
      color: 'text-[#8B6B4A]',
      bgColor: 'bg-[#8B6B4A]/5',
      borderGlow: 'hover:border-[#8B6B4A]/30'
    }
  ];

  return (
    <section id="process" className="py-24 md:py-32 relative bg-[#EFE7DD]/40 border-y border-[#8B6B4A]/10" data-testid="process-section">
      {/* Background elements */}
      <div className="absolute top-10 left-10 glowing-blob blob-beige opacity-[0.05]" />
      <div className="absolute bottom-10 right-10 glowing-blob blob-gold opacity-[0.05]" />

      <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
        
        {/* Header */}
        <div className="text-center mb-24">
          <span className="text-xs font-semibold tracking-widest uppercase text-transparent bg-clip-text bg-gradient-to-r from-[#8B6B4A] to-[#C8A97E] font-poppins mb-3 block">
            Our Blueprint
          </span>
          <h2 className="text-4xl md:text-5xl font-bold font-space text-[#3E2F24] tracking-tight">
            How We Bring Ideas to Life
          </h2>
          <p className="text-[#6B625B] text-base sm:text-lg font-poppins max-w-2xl mx-auto mt-4">
            A structured, collaborative engineering process designed to eliminate risks and deliver high-value software.
          </p>
        </div>

        {/* Timeline Path Container */}
        <div className="relative max-w-5xl mx-auto">
          
          {/* Vertical Center line (Desktop only) */}
          <div className="absolute left-1/2 top-4 bottom-4 w-px bg-[#8B6B4A]/10 -translate-x-1/2 hidden lg:block" />

          {/* Timeline Nodes */}
          <div className="space-y-12 lg:space-y-16">
            {steps.map((step, idx) => {
              const isEven = idx % 2 === 0;

              return (
                <div 
                  key={step.number} 
                  className={`flex flex-col lg:flex-row items-center gap-8 lg:gap-0 relative ${
                    isEven ? 'lg:flex-row-reverse' : ''
                  }`}
                  data-testid={`timeline-step-${idx}`}
                >
                  
                  {/* Glowing Node Dot on Center Line */}
                  <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-4 h-4 rounded-full bg-[#F8F5F0] border-2 border-[#8B6B4A]/20 hidden lg:flex items-center justify-center z-25">
                    <div className="w-1.5 h-1.5 rounded-full bg-[#8B6B4A] animate-pulse" />
                  </div>

                  {/* Half 1: Card Content */}
                  <motion.div 
                    initial={{ opacity: 0, x: isEven ? 25 : -25 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true, margin: '-100px' }}
                    transition={{ duration: 0.6, ease: 'easeOut' }}
                    className="w-full lg:w-[45%]"
                  >
                    <div className={`p-8 rounded-3xl bg-white/40 border border-[#8B6B4A]/10 ${step.borderGlow} transition-all duration-300 relative group backdrop-blur-md`}>
                      <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center gap-3">
                          <div className={`w-10 h-10 rounded-xl ${step.bgColor} ${step.color} flex items-center justify-center`}>
                            <step.icon className="w-5 h-5" />
                          </div>
                          <h3 className="text-base sm:text-lg font-bold font-space text-[#3E2F24]">{step.title}</h3>
                        </div>
                        <span className="font-space font-bold text-2xl text-[#8B6B4A]/15 group-hover:text-[#8B6B4A]/30 transition-colors">
                          {step.number}
                        </span>
                      </div>
                      
                      <p className="text-[#6B625B] font-poppins text-xs sm:text-sm leading-relaxed">
                        {step.description}
                      </p>
                    </div>
                  </motion.div>

                  {/* Spacer for Alternate Layout */}
                  <div className="w-full lg:w-[10%] flex justify-center items-center" />

                  {/* Half 2: Empty Spacer (Desktop only) */}
                  <div className="w-full lg:w-[45%] hidden lg:block" />

                </div>
              );
            })}
          </div>

        </div>

      </div>
    </section>
  );
}
